import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, MenuIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useBooking } from './BookingModal';
import { brand, contact, nav, whatsappUrl } from '../data/site';
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const {
    openBooking
  } = useBooking();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  return <>
      <header className={`fixed inset-x-0 top-0 z-[80] transition-all duration-500 ${scrolled ? 'border-b border-line bg-surface/80 py-3 backdrop-blur-xl' : 'border-b border-transparent py-5'}`} data-id="element-333" data-mp="enkf6dijo9lr">
        <nav aria-label="Primary" className="mx-auto flex w-full max-w-container items-center justify-between px-5 sm:px-8" data-id="element-334" data-mp="okff90ugui25">
          <Logo data-id="element-335" data-mp="3s27aqp7t9k53" />

          <ul className="hidden items-center gap-9 lg:flex" data-id="element-336" data-mp="3s1nt79m2oxrl">
            {nav.map(item => <li key={item.to} data-id="element-337" data-mp="vw3ucg4dp5u">
                <NavLink to={item.to} className={({
              isActive
            }) => `group relative inline-block py-1 text-[0.8rem] font-medium uppercase tracking-[0.14em] transition-colors ${isActive ? 'text-brand' : 'text-ink hover:text-brand'}`} data-cursor="hover" data-id="element-338" data-mp="ul36lhz0xiy">
                  {({
                isActive
              }) => <>
                      {item.label}
                      <span aria-hidden="true" className={`absolute -bottom-0.5 left-0 h-px bg-brand transition-all duration-500 ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-339" data-mp="2wbwy965g8in9" />
                    </>}
                </NavLink>
              </li>)}
          </ul>

          <div className="flex items-center gap-2.5" data-id="element-340" data-mp="2dfp0soiohc2i">
            <ThemeToggle data-id="element-341" data-mp="2sbczxz9538zj" />
            <button type="button" onClick={openBooking} className="hidden items-center gap-2 bg-brand px-5 py-3 text-[0.75rem] font-medium uppercase tracking-[0.16em] text-white transition-colors hover:bg-brand-deep sm:inline-flex" data-cursor="label" data-cursor-label="Let's talk" data-id="element-342" data-mp="2sbwfxaf8bnax">
              Book a Consultation
              <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-343" data-mp="a6vckxv95sm0" />
            </button>
            <button type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" aria-expanded={menuOpen} className="flex h-9 w-9 items-center justify-center border border-line text-ink transition-colors hover:border-brand hover:text-brand lg:hidden" data-id="element-344" data-mp="f1sjnz8z1rw9">
              <MenuIcon className="h-4 w-4" data-id="element-345" data-mp="xuwf9q5r2eti" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence data-id="element-346" data-mp="k3rnfa8miqtq">
        {menuOpen ? <motion.div className="fixed inset-0 z-[95] flex flex-col bg-surface" initial={{
        clipPath: 'inset(0 0 100% 0)'
      }} animate={{
        clipPath: 'inset(0 0 0% 0)'
      }} exit={{
        clipPath: 'inset(0 0 100% 0)'
      }} transition={{
        duration: 0.6,
        ease: [0.76, 0, 0.24, 1]
      }} role="dialog" aria-modal="true" aria-label="Site menu" data-id="element-347" data-mp="k0vpvn0ou45t">
            <div className="flex items-center justify-between px-5 py-5 sm:px-8" data-id="element-348" data-mp="3k33xr7rj6r7d">
              <Logo onClick={() => setMenuOpen(false)} data-id="element-349" data-mp="3f3rcusk7zu7f" />
              <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="flex h-10 w-10 items-center justify-center border border-line text-ink transition-colors hover:border-brand hover:text-brand" data-id="element-350" data-mp="3f4j09g3nylmm">
                <XIcon className="h-5 w-5" data-id="element-351" data-mp="6oprxojhkggn" />
              </button>
            </div>

            <nav aria-label="Mobile" className="flex flex-1 flex-col justify-center px-5 sm:px-8" data-id="element-352" data-mp="1vwkuu0jow1e">
              <ul data-id="element-353" data-mp="3u08jx7ldmmo6">
                {nav.map((item, index) => <li key={item.to} className="overflow-hidden border-b border-line" data-id="element-354" data-mp="ue21ipiq6qjh" data-mpi={index}>
                    <motion.div initial={{
                y: '110%'
              }} animate={{
                y: '0%'
              }} transition={{
                duration: 0.7,
                delay: 0.12 + index * 0.06,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-355" data-mp="pn21hbeb7scr">
                      <Link to={item.to} onClick={() => setMenuOpen(false)} className="flex items-baseline justify-between py-4" data-id="element-356" data-mp="2h8oq4b8h2yv7">
                        <span className="font-display text-[2.6rem] font-extrabold uppercase leading-none tracking-tightest sm:text-6xl" data-id="element-357" data-mp="2r6bgpuz4wev0">
                          {item.label}
                        </span>
                        <span className="label text-muted" data-id="element-358" data-mp="310ldrpz0u1c2">0{index + 1}</span>
                      </Link>
                    </motion.div>
                  </li>)}
              </ul>

              <motion.div className="mt-9 flex flex-col gap-3" initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45,
            duration: 0.5
          }} data-id="element-359" data-mp="39x2q2wj4g59e">
                <button type="button" onClick={() => {
              setMenuOpen(false);
              openBooking();
            }} className="flex items-center justify-center gap-2 bg-brand px-6 py-4 text-[0.8rem] font-medium uppercase tracking-[0.16em] text-white" data-id="element-360" data-mp="19xpc2roh4hc9">
                  Book a Consultation
                  <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-361" data-mp="1tjlpexfk2t2x" />
                </button>
                <a href={whatsappUrl} target="_blank" rel="noreferrer noopener" className="flex items-center justify-center gap-2 border border-line px-6 py-4 text-[0.8rem] font-medium uppercase tracking-[0.16em] text-ink" data-id="element-362" data-mp="27jdatj8ejbqy">
                  WhatsApp {contact.whatsappDisplay}
                </a>
              </motion.div>
            </nav>

            <div className="flex flex-col gap-1 px-5 pb-8 sm:px-8" data-id="element-363" data-mp="2jvfltrh2y2nv">
              <a href={`mailto:${contact.email}`} className="text-sm text-muted hover:text-brand" data-id="element-364" data-mp="2ospah4wnaaan">
                {contact.email}
              </a>
              <p className="label text-muted" data-id="element-365" data-mp="2a303uu5s9e2z">{brand.hours}</p>
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </>;
}